/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ScheduleTaskTodoEventType,
  ScheduleType,
  UserSystemRole,
  userSystemRoleToBitSet,
} from '@gh/builder-shared-data';

import {
  EnumDictionary,
  FormState,
  FormStateBuilder,
  getDialogInitData,
  NumberTypes,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import {
  AutocompleteLoadMoreEvent,
  DialogInput,
  FormMode,
  getControl,
  getControlValue,
  setControlValue,
  WaitingService,
} from '@gh/core-ui';

import { ScheduleTaskDetailsBusinessService } from '../../../resources/schedule-task-details-business.service';
import {
  ScheduleTaskTodoListItem,
  ScheduleTaskUserAutocomplete,
} from '../../../resources/schedule-task-details.model';

@Component({
  standalone: false,
  selector: 'gh-schedule-task-todo-dialog',
  templateUrl: 'schedule-task-todo-dialog.component.html',
  styleUrls: ['schedule-task-todo-dialog.component.scss'],
})
export class ScheduleTaskDetailsTodoDialogComponent implements OnInit, DialogInput<ScheduleTaskTodoListItem> {
  input: any;
  form: FormState<ScheduleTaskTodoListItem>;
  systemRoles: EnumDictionary<UserSystemRole>;
  todoEventTypes: EnumDictionary<ScheduleTaskTodoEventType>;

  constructor(private dialogRef: MatDialogRef<any>,
              @Inject(MAT_DIALOG_DATA) private dialogData: any,
              private waitingService: WaitingService,
              private businessService: ScheduleTaskDetailsBusinessService,
              private formStateBuilder: FormStateBuilder) {
  }

  get title(): string {
    return {
      [FormMode.CreateMode]: 'lbl_add_todo',
      [FormMode.EditMode]: 'lbl_edit_todo',
      [FormMode.ViewMode]: 'lbl_todo',
    }[this.form.mode];
  }

  ngOnInit(): void {
    const { systemRoles, salesSystemRoles, todoEventTypes } = getDialogInitData(this.dialogData);
    this.systemRoles = this.systemRoles = this.isSalesScheduleTemplate() ? salesSystemRoles : systemRoles;

    this.todoEventTypes = todoEventTypes;
    this.form = this.formStateBuilder.buildPlain({
      mode: this.input.mode,
      enabled: whenCreateOrEditMode(),
      value: {
        systemRole: this.input.systemRole,
        user: this.input.user,
        days: this.input.days,
        event: this.input.event,
        memo: this.input.memo,
      },
      children: {
        systemRole: void 0,
        user: { required: true },
        days: { required: true, type: NumberTypes.Days },
        event: { required: true },
        memo: { required: true },
      },
    });
  }

  onSave(): void {
    if (this.form.group.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.dialogRef.close(this.form.submitValue);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSystemNameChanged(): void {
    getControl(this.form.group, 'user').reset();
  }

  onSelectUser(user: Maybe<ScheduleTaskUserAutocomplete>): void {
    if (user) {
      setControlValue(this.form.group, 'user', {
        userName: user.userName,
        userId: user.userId,
      });
    }
  }

  loadAutocompleteUsers({ query, load }: AutocompleteLoadMoreEvent): void {
    load(this.businessService.getAutocompleteUser({
      ...query,
      filter: {
        name: query.filter,
        systemRoles: userSystemRoleToBitSet(getControlValue(this.form.group, 'systemRole')),
      },
    }));
  }

  private isSalesScheduleTemplate(): boolean {
    return this.input.scheduleType === ScheduleType.Sales;
  }
}
